import type { ClaimSubmissionRequest, ClaimType, ViewClaimDetailsResponse } from 'all-in-one-shared-api';
import { Moment } from 'moment';
import { getDate } from 'utils/datetime';
import { getFilename } from '../../../utils/getFilename';
import { ClaimFormState, excludeRemovedDocuments } from '../schema';
import { PreviousClaimUtils } from './previousClaimUtils';

export interface NewClaimFormInput {
  patientMemberType: string;
  patientName: string;
  claimTypeObject?: ClaimType; // For form input
  claimTypeCode?: string; // ClaimType code
  claimTypeName?: string; // ClaimType Description
  benefitNames?: string[];
  doctorId: string;
  doctorName: string;
  diagnosisCode: string;
  diagnosisName?: string;
  documentCategories: string[];
  treatmentDateMoment?: Moment;
  receiptCurrency: string;
  receiptAmount: string[];
  receipts?: File[];
  referralLetters?: File[];
  otherDocuments?: File[];
  prevClaimSettlements?: File[];
  isPartialClaim: boolean;
  isFirstClaim?: boolean;
  firstClaimWithGenerali?: boolean;
  anotherPolicyNumber?: string;
  anotherMemberId?: string;
  otherInsurerName?: string;
  firstClaimCurrency?: string;
  firstClaimAmount?: string;
  editAllowed?: boolean;
}

export function setNewClaimFormInput(claimTypeOptions: ClaimType[], isEnglish: boolean) {
  return (values: Readonly<ViewClaimDetailsResponse>): NewClaimFormInput => {
    const { patientMemberType, patientName, benefits, claimType, doctor, diagnosis, receiptAmounts } = values;
    const formBenefitCodes = benefits.map(({ code }) => code || '');

    const benefitOptions = claimTypeOptions.find(({ code }) => code === 'OP')?.benefits || [];
    const filteredClaimType = claimTypeOptions.find(({ code }) => claimType?.code === (code || '')) || {};
    const filteredBenefits = benefitOptions.filter(({ code }) => formBenefitCodes.includes(code || ''));

    let documentCategories: string[] = [];

    if (filteredClaimType.code === 'OP') {
      documentCategories = filteredBenefits.flatMap(({ requiredAttachments, chineseRequiredAttachments }) =>
        isEnglish ? requiredAttachments || [] : chineseRequiredAttachments || [],
      );
    } else {
      documentCategories = isEnglish
        ? filteredClaimType?.requiredAttachments || []
        : filteredClaimType?.chineseRequiredAttachments || [];
    }

    return {
      patientMemberType,
      patientName,
      claimTypeCode: claimType?.code,
      ...(formBenefitCodes?.length > 0 ? { benefitNames: formBenefitCodes } : {}),
      claimTypeName: isEnglish ? claimType?.description : claimType?.chineseDescription,
      claimTypeObject: {
        ...filteredClaimType,
        ...(filteredBenefits?.length > 0 ? { benefits: filteredBenefits } : {}),
      },
      isPartialClaim: false,
      doctorId: doctor?.code,
      doctorName: PreviousClaimUtils.getDoctorName(doctor, isEnglish),
      diagnosisCode: diagnosis?.code || '',
      diagnosisName: isEnglish ? diagnosis?.description : diagnosis?.chineseDescription,
      receiptCurrency: receiptAmounts?.[0]?.currency || '',
      receiptAmount: receiptAmounts?.map(({ amount }) => amount) || [],
      documentCategories,
    };
  };
}

export function setEditClaimFormInput(
  claimTypeOptions: ClaimType[],
  isEnglish: boolean,
  values: Readonly<ViewClaimDetailsResponse>,
): NewClaimFormInput {
  const formValues = setNewClaimFormInput(claimTypeOptions, isEnglish)(values);
  const {
    anotherMemberId,
    anotherPolicyNumber,
    otherInsurerName,
    treatmentDate,
    otherClaimAmount,
    fullClaim,
    firstClaim,
    firstClaimWithGenerali,
    editAllowed,
  } = values;
  const treatmentDateMoment = getDate(treatmentDate, 'YYYY-MM-DD') as Moment;

  // eslint-disable-next-line
  const buildUploadFileList = (filePaths?: string[]): any[] => {
    if (!filePaths) return [];
    return filePaths.map((filePath: string) => ({
      uid: filePath,
      name: getFilename(filePath),
    }));
  };

  return {
    ...formValues,
    treatmentDateMoment,
    otherInsurerName,
    firstClaimAmount: otherClaimAmount?.amount,
    firstClaimCurrency: otherClaimAmount?.currency,
    anotherMemberId,
    anotherPolicyNumber,
    isPartialClaim: !fullClaim,
    isFirstClaim: firstClaim,
    firstClaimWithGenerali,
    editAllowed,
    receipts: buildUploadFileList(values['receiptFiles']),
    referralLetters: buildUploadFileList(values['refLetters']),
    otherDocuments: buildUploadFileList(values['otherDocs']),
  };
}

const removeDeletedDocuments = (values: ClaimFormState) => {
  values['receipts'] = excludeRemovedDocuments(values['receipts']);
  values['referralLetters'] = excludeRemovedDocuments(values['referralLetters']);
  values['otherDocuments'] = excludeRemovedDocuments(values['otherDocuments']);
  values['prevClaimSettlements'] = excludeRemovedDocuments(values['prevClaimSettlements']);
};

export function getClaimSubmissionRequest(values: ClaimFormState): ClaimSubmissionRequest {
  let partialClaimData = {};
  removeDeletedDocuments(values);
  const {
    claimType,
    patientMemberType,
    doctorId,
    doctorName,
    diagnosisCode,
    receipts,
    referralLetters,
    otherDocuments,

    claimTypeObject,
    treatmentDate,
    currency,

    partialClaim,
    isFirstClaim,
    firstClaimWithGenerali,
    anotherPolicyNumber,
    anotherMemberId,
    otherInsurerName,
    firstClaimCurrency,
    firstClaimAmount,
    prevClaimSettlements,
  } = values;
  if (partialClaim) {
    if (isFirstClaim || firstClaimWithGenerali) {
      partialClaimData = {
        anotherPolicyNumber,
        anotherMemberId,
      };
    } else if (!isFirstClaim && !firstClaimWithGenerali) {
      partialClaimData = {
        otherInsurerName,
        ...(firstClaimCurrency && firstClaimAmount
          ? {
              otherClaimAmount: {
                currency: firstClaimCurrency,
                amount: firstClaimAmount,
              },
            }
          : {}),
        prevClaimSettlements: prevClaimSettlements || [],
      };
    }
  }

  const claimSubmissionRequest: ClaimSubmissionRequest = {
    patientMemberType,
    doctorId,
    doctorName,
    diagnosisCode: diagnosisCode || '',
    fullClaim: !partialClaim,
    firstClaim: isFirstClaim || false,
    firstClaimWithGenerali: firstClaimWithGenerali || false,
    claimType: claimType === 'OP' ? claimType : (claimTypeObject && claimTypeObject[0]) || '',
    benefitCodes: claimType === 'OP' && claimTypeObject ? claimTypeObject?.map((claim) => claim.replace('__', '')) : [],
    treatmentDate: treatmentDate || '',
    receiptAmounts: values.receiptAmounts.map((amount: string) => ({ currency: currency, amount })),
    receipts: receipts || [],
    referralLetters: referralLetters || [],
    otherDocuments: otherDocuments || [],
    prevClaimSettlements: prevClaimSettlements || [],
    ...partialClaimData,
  };

  return claimSubmissionRequest;
}
